import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import SEOHead from './SEOHead';
import LandingFooter from './LandingFooter';
import RichText, { stripRich } from './RichText';
import { getSolution } from '../content/solutions';
import './landing.css';

const serif = { fontFamily: '"DM Serif Display", Georgia, serif' };
const ACCENT = '#1a5fff';
const BASE_URL = 'https://digihr.in';

const buildSchema = (s) => [
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `DigiHR ${s.nav}`,
    url: `${BASE_URL}/${s.slug}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Android',
    description: s.description,
    offers: { '@type': 'Offer', price: '99', priceCurrency: 'INR', url: `${BASE_URL}/pricing` },
    publisher: { '@id': `${BASE_URL}/#organization` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: s.faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: stripRich(a) },
    })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Features', item: `${BASE_URL}/features` },
      { '@type': 'ListItem', position: 3, name: s.nav, item: `${BASE_URL}/${s.slug}` },
    ],
  },
];

const SolutionPage = ({ slug }) => {
  const s = getSolution(slug);
  const [before, em, after] = s.h1;

  return (
    <>
      <SEOHead title={s.title} description={s.description} keywords={s.keywords} canonical={`/${s.slug}`} schema={buildSchema(s)} />

      {/* Page Hero */}
      <section style={{ padding: '60px 5% 52px', background: 'linear-gradient(160deg,#f0f4ff 0%,#ffffff 100%)', borderBottom: '1px solid #e4e7ed' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: '#717888', marginBottom: 16 }}>
            <Link to="/" style={{ color: '#717888', textDecoration: 'none' }}>Home</Link> / <Link to="/features" style={{ color: '#717888', textDecoration: 'none' }}>Features</Link> / <span style={{ color: '#3a3f4a' }}>{s.nav}</span>
          </nav>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: ACCENT, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 12 }}>{s.eyebrow}</div>
          <h1 style={{ ...serif, fontSize: 'clamp(36px,4.5vw,56px)', lineHeight: 1.1, letterSpacing: '-.03em', color: '#0d1117', maxWidth: 720, marginBottom: 16 }}>
            {before}<em style={{ fontStyle: 'italic', color: ACCENT }}>{em}</em>{after}
          </h1>
          <p style={{ fontSize: 17, color: '#3a3f4a', lineHeight: 1.65, maxWidth: 640, marginBottom: 24 }}>{s.intro}</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 12, background: ACCENT, color: '#fff', fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>Book a free Demo</Link>
            <Link to="/pricing" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 20px', borderRadius: 12, background: 'transparent', color: '#3a3f4a', border: '1px solid #e4e7ed', fontSize: 14, fontWeight: 500, textDecoration: 'none' }}>View pricing</Link>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section style={{ padding: '80px 5%' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,40px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117', marginBottom: 40 }}>What you get</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }} className="solution-grid-resp">
            {s.highlights.map(({ title, desc }) => (
              <div key={title} style={{ background: '#fff', border: '1px solid #e4e7ed', borderRadius: 20, padding: 28 }}>
                <CheckCircle2 size={22} color={ACCENT} strokeWidth={1.75} style={{ marginBottom: 14 }} />
                <h3 style={{ fontSize: 17, fontWeight: 600, color: '#0d1117', marginBottom: 8 }}>{title}</h3>
                <p style={{ fontSize: 14, color: '#3a3f4a', lineHeight: 1.6, margin: 0 }}><RichText text={desc} /></p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ padding: '80px 5%', background: '#f6f7f9', borderTop: '1px solid #e4e7ed', borderBottom: '1px solid #e4e7ed' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,40px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117', marginBottom: 40 }}>How it works</h2>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 24 }} className="solution-steps-resp">
            {s.steps.map(({ title, desc }, i) => (
              <li key={title}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: ACCENT, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 15, marginBottom: 14 }}>{i + 1}</div>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0d1117', marginBottom: 6 }}>{title}</h3>
                <p style={{ fontSize: 14, color: '#3a3f4a', lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ padding: '80px 5%' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,40px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#0d1117', marginBottom: 32 }}>Frequently asked questions</h2>
          {s.faqs.map(({ q, a }) => (
            <div key={q} style={{ borderBottom: '1px solid #e4e7ed', padding: '22px 0' }}>
              <h3 style={{ fontSize: 17, fontWeight: 600, color: '#0d1117', marginBottom: 8 }}>{q}</h3>
              <p style={{ fontSize: 15, color: '#3a3f4a', lineHeight: 1.7, margin: 0 }}><RichText text={a} /></p>
            </div>
          ))}
        </div>
      </section>

      {/* Related */}
      <section style={{ padding: '0 5% 80px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#717888', letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 16 }}>Related modules</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {s.related.map(r => getSolution(r)).map(r => (
              <Link key={r.slug} to={`/${r.slug}`} style={{ padding: '10px 18px', borderRadius: 12, border: '1px solid #e4e7ed', color: '#0d1117', fontSize: 14, fontWeight: 500, textDecoration: 'none', background: '#fff' }}>{r.nav} →</Link>
            ))}
            <Link to="/features" style={{ padding: '10px 18px', borderRadius: 12, border: '1px solid #e4e7ed', color: '#0d1117', fontSize: 14, fontWeight: 500, textDecoration: 'none', background: '#fff' }}>All features →</Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 5%', background: 'linear-gradient(135deg,#0d1117 0%,#1a2540 100%)', textAlign: 'center', color: '#fff', position: 'relative', overflow: 'hidden' }} className="cta-band-radial">
        <div style={{ position: 'relative', maxWidth: 640, margin: '0 auto' }}>
          <h2 style={{ ...serif, fontSize: 'clamp(28px,3.5vw,42px)', lineHeight: 1.15, letterSpacing: '-.025em', color: '#fff', marginBottom: 14 }}>See {s.nav.toLowerCase()} in action</h2>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,.6)', marginBottom: 32 }}>Book a personalised 30-minute demo tailored to your team size and industry.</p>
          <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '14px 32px', borderRadius: 20, background: ACCENT, color: '#fff', fontSize: 15, fontWeight: 500, textDecoration: 'none' }}>
            Book a free Demo →
          </Link>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,.4)', marginTop: 14 }}>30-day free trial · No credit card required</div>
        </div>
      </section>

      <LandingFooter />

      <style>{`@media(max-width:900px){ .solution-grid-resp, .solution-steps-resp{ grid-template-columns:1fr !important; } }`}</style>
    </>
  );
};

export default SolutionPage;
